import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ResiduosSelector from "@/components/puntolimpio/ResiduosSelector";
import QrScannerModal from "@/components/puntolimpio/QrScannerModal";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Recycle, CheckCircle2, Loader2, ScanLine, Download, WifiOff, RefreshCw, QrCode, Clock, MapPin, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { validateDniNie, normalizeDniNie, validateCif } from "@/lib/dni";
import { getCatalogoResiduos, createPuntoLimpioCheckin, getPuntoLimpioInfo } from "@/api";
import { enqueueCheckin, flushQueue, getQueue, isNetworkError } from "@/lib/offlineQueue";
import { getErrorMessage } from "@/lib/errors";
import { buildQrPayload, QR_FIELDS } from "@/lib/puntolimpioQr";
import GenerarQrModal from "@/components/puntolimpio/GenerarQrModal";
import PuntoLimpioMapaUbicacion from "@/components/puntolimpio/PuntoLimpioMapaUbicacion";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const initialForm = {
  tipo_solicitante: "ciudadano", nombre_apellidos: "", dni_nie: "", razon_social: "", cif: "",
  codigopostal: "", direccion: "", numero: "",
  piso: "", letra: "", detalles: "", email: "", deseaemail: true,
  apuntado: false, acepta_clausula: false,
};

// Extrae los dígitos de valores como "2º" o "2B" (notación habitual de piso/portal en
// España) en vez de perderlos silenciosamente al convertir a número.
function toIntOrNull(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits ? parseInt(digits, 10) : null;
}

export default function PuntoLimpio() {
  const [catalogo, setCatalogo] = useState([]);
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [residuos, setResiduos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [showGenerarQr, setShowGenerarQr] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [pendingCount, setPendingCount] = useState(getQueue().length);
  const [hasPendingResiduo, setHasPendingResiduo] = useState(false);
  const qrDownloadRef = useRef(null);
  const residuosSelectorRef = useRef(null);

  useEffect(() => {
    getCatalogoResiduos().then(setCatalogo).catch(() => {});
    getPuntoLimpioInfo().then(setInfo).catch(() => {});
  }, []);

  const syncPending = () => {
    flushQueue(createPuntoLimpioCheckin, {
      onItemSynced: (item, serverResult) => {
        toast.success(`Visita sincronizada: ${serverResult.codigo} (registrada sin conexión el ${new Date(item.createdAt).toLocaleString()})`);
      },
    }).then(({ synced }) => {
      setPendingCount(getQueue().length);
      if (synced > 0) toast.success(`${synced} visita(s) pendiente(s) sincronizada(s) con el servidor`);
    });
  };

  useEffect(() => {
    const handleOnline = () => { setIsOffline(false); syncPending(); };
    const handleOffline = () => setIsOffline(true);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    if (navigator.onLine) syncPending();
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const isEmpresa = form.tipo_solicitante === "empresa";
  const dniValid = form.dni_nie && validateDniNie(form.dni_nie);

  const handleQrScanned = useCallback((text) => {
    try {
      const data = JSON.parse(text);
      if (data.tipo !== "puntolimpio_ciudadano_v1") throw new Error("formato no reconocido");
      update({
        ...Object.fromEntries(QR_FIELDS.map((f) => [f, data[f] ?? ""])),
        tipo_solicitante: data.tipo_solicitante === "empresa" ? "empresa" : "ciudadano",
      });
      toast.success("Datos rellenados desde tu QR");
    } catch {
      toast.error("Ese código QR no es válido para este formulario");
    } finally {
      setShowScanner(false);
    }
  }, []);

  const downloadQr = () => {
    const canvas = qrDownloadRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `qr-puntolimpio-${resultado.codigo}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const canSubmit =
    (isEmpresa
      ? form.razon_social.trim().length > 1 && validateCif(form.cif || "")
      : form.nombre_apellidos.trim().length > 2 && dniValid) &&
    form.direccion.trim().length > 2 &&
    (!form.email.trim() || /\S+@\S+\.\S+/.test(form.email)) &&
    (residuos.length > 0 || hasPendingResiduo) &&
    form.acepta_clausula;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const pendingEntry = residuosSelectorRef.current?.getPendingEntry();
    const finalResiduos = pendingEntry ? [...residuos, pendingEntry] : residuos;
    const payload = {
      ...form,
      nombre_apellidos: isEmpresa ? null : form.nombre_apellidos,
      dni_nie: isEmpresa ? null : normalizeDniNie(form.dni_nie),
      razon_social: isEmpresa ? form.razon_social : null,
      cif: isEmpresa ? form.cif : null,
      apuntado: isEmpresa ? false : form.apuntado,
      codigopostal: toIntOrNull(form.codigopostal),
      numero: toIntOrNull(form.numero),
      piso: toIntOrNull(form.piso),
      email: form.email.trim() || null,
      deseaemail: !!form.email.trim() && form.deseaemail,
      residuos: finalResiduos,
    };

    if (!navigator.onLine) {
      enqueueCheckin(payload);
      setPendingCount(getQueue().length);
      setResultado({ ...payload, codigo: `PL-PENDIENTE-${Date.now().toString().slice(-6)}`, pendiente: true });
      setSubmitting(false);
      window.scrollTo(0, 0);
      return;
    }

    try {
      const res = await createPuntoLimpioCheckin(payload);
      setResultado(res);
      window.scrollTo(0, 0);
    } catch (err) {
      if (isNetworkError(err)) {
        enqueueCheckin(payload);
        setPendingCount(getQueue().length);
        setResultado({ ...payload, codigo: `PL-PENDIENTE-${Date.now().toString().slice(-6)}`, pendiente: true });
        window.scrollTo(0, 0);
      } else if (err?.response?.status === 429) {
        setError("Demasiadas solicitudes seguidas. Espera un minuto y vuelve a intentarlo.");
      } else {
        const msg = getErrorMessage(err, "No se pudo registrar tu visita. Revisa los datos introducidos e inténtalo de nuevo.");
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (resultado) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <section className="pt-32 pb-20 px-4 bg-gray-50 min-h-screen flex items-center">
          <div className="max-w-lg mx-auto text-center bg-white border border-gray-200 rounded-2xl p-10 shadow-sm" data-testid="checkin-confirmation">
            {resultado.pendiente ? (
              <>
                <WifiOff className="w-14 h-14 text-amber-500 mx-auto mb-4" />
                <h1 className="font-heading font-bold text-2xl text-municipal mb-2">Visita guardada en este dispositivo</h1>
                <p className="text-gray-500 mb-6" data-testid="checkin-pendiente-msg">
                  No hay conexión ahora mismo. Hemos guardado tu visita y se enviará automáticamente al
                  servidor en cuanto se recupere la señal en este mismo dispositivo.
                </p>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-14 h-14 text-valoriza-green mx-auto mb-4" />
                <h1 className="font-heading font-bold text-2xl text-municipal mb-2">Visita pre-registrada</h1>
                <p className="text-gray-500 mb-6">Preséntate en el Punto Limpio con este código. El personal confirmará tu entrada.</p>
              </>
            )}
            <div className="bg-municipal/5 border border-municipal/20 rounded-xl py-4 mb-6">
              <span className="text-2xl font-bold text-municipal tracking-wider" data-testid="checkin-codigo">{resultado.codigo}</span>
            </div>
            {resultado.tipo_solicitante !== "empresa" && (
              <div className="flex items-start gap-2 text-left bg-amber-50 border border-amber-300 rounded-xl p-4 mb-6 text-sm text-amber-800" data-testid="bonificacion-aviso-confirmacion">
                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                <span>
                  <strong>Importante:</strong> este pre-registro no cuenta por sí solo para la bonificación.
                  Debes acudir físicamente al Punto Limpio Móvil y mostrar este código QR al personal
                  para que se registre tu visita.
                </span>
              </div>
            )}
            <div ref={qrDownloadRef} className="flex flex-col items-center mb-6">
              <p className="text-xs text-gray-400 mb-3">Guarda este QR para no tener que rellenar tus datos la próxima vez</p>
              <QRCodeCanvas value={buildQrPayload(resultado)} size={160} fgColor="#BD3742" data-testid="checkin-qr-canvas" />
              <Button variant="ghost" size="sm" onClick={downloadQr} className="mt-2 text-municipal" data-testid="checkin-qr-download-btn">
                <Download className="w-4 h-4 mr-1" /> Descargar mi QR
              </Button>
            </div>
            <Link to="/"><Button className="bg-municipal hover:bg-municipal-dark text-white">Volver al inicio</Button></Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <section className="pt-28 pb-10 px-4 bg-[#5c927a] text-white">
        <div className="max-w-3xl mx-auto text-center">
          <Recycle className="w-10 h-10 mx-auto mb-3" />
          <h1 className="font-heading font-bold text-3xl sm:text-4xl mb-3">Punto Limpio Móvil</h1>
          <p className="text-white/85 max-w-xl mx-auto">
            Registra tu visita antes de acercarte y agiliza la entrega de residuos peligrosos de uso
            doméstico (aceites, baterías, aerosoles, fluorescentes...) en el Punto Limpio Móvil.
          </p>
          {info && (
            <div className="flex items-center justify-center gap-5 mt-5 flex-wrap text-sm" data-testid="puntolimpio-info-banner">
              <span className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5">
                <Clock className="w-4 h-4" /> Todos los sábados, {info.horario}
              </span>
              <span className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5">
                <MapPin className="w-4 h-4" /> Próxima ubicación: {info.ubicacion}
              </span>
            </div>
          )}
          <p className="text-white/70 text-xs mt-3 max-w-md mx-auto" data-testid="puntolimpio-ubicacion-regla">
            Los 2 primeros sábados de cada mes: <strong className="text-white">Parque 1º de Mayo</strong>. El resto de sábados: <strong className="text-white">Plaza de Gallarta</strong>.
          </p>
          {info && <PuntoLimpioMapaUbicacion info={info} />}
        </div>
      </section>

      <section className="py-14 px-4 bg-gray-50">
        {!isEmpresa && (
          <div className="max-w-3xl mx-auto mb-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="bonificacion-aviso-formulario">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              <strong>Importante:</strong> rellenar este formulario no concede la bonificación por sí solo.
              Solo se bonifica si acudes físicamente al Punto Limpio Móvil y el personal registra tu
              visita escaneando tu código QR.
            </span>
          </div>
        )}
        {(isOffline || pendingCount > 0) && (
          <div
            className="max-w-3xl mx-auto mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
            data-testid="offline-banner"
          >
            <span className="flex items-center gap-2">
              <WifiOff className="w-4 h-4" />
              {isOffline
                ? "Sin conexión: tu visita se guardará en este dispositivo y se enviará al recuperar señal."
                : `${pendingCount} visita(s) guardada(s) en este dispositivo pendiente(s) de enviar.`}
            </span>
            {!isOffline && pendingCount > 0 && (
              <Button type="button" size="sm" variant="outline" className="border-amber-400 text-amber-800" onClick={syncPending} data-testid="sync-pending-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reintentar envío
              </Button>
            )}
          </div>
        )}
        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto bg-white border border-gray-200 rounded-2xl p-6 sm:p-10 shadow-sm" data-testid="puntolimpio-checkin-form">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
            <h2 className="font-heading font-bold text-xl text-municipal">Datos de tu visita</h2>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setShowGenerarQr(true)} className="border-dorado text-dorado" data-testid="generar-qr-btn">
                <QrCode className="w-4 h-4 mr-2" /> Generar mi QR
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setShowScanner(true)} className="border-municipal text-municipal" data-testid="scan-qr-btn">
                <ScanLine className="w-4 h-4 mr-2" /> Escanear mi QR
              </Button>
            </div>
          </div>
          <p className="text-xs text-gray-400 mb-5 -mt-3">
            ¿Es tu primera vez? Pulsa "Generar mi QR" para crear tu código antes de venir, sin
            tener que registrar una visita todavía.
          </p>

          <div className="mb-5">
            <Label className="text-sm font-semibold text-municipal mb-3 block">¿Quién registra la visita?</Label>
            <RadioGroup
              value={form.tipo_solicitante}
              onValueChange={(v) => update({ tipo_solicitante: v })}
              className="grid sm:grid-cols-2 gap-3"
              data-testid="checkin-tipo-solicitante-group"
            >
              <div className={`flex items-center space-x-2 border-2 rounded-xl px-4 py-3 ${form.tipo_solicitante === "ciudadano" ? "border-municipal bg-municipal/5" : "border-gray-200"}`}>
                <RadioGroupItem value="ciudadano" id="checkin-solicitante-ciudadano" data-testid="checkin-solicitante-ciudadano" />
                <Label htmlFor="checkin-solicitante-ciudadano" className="font-normal cursor-pointer text-sm">Soy un ciudadano</Label>
              </div>
              <div className={`flex items-center space-x-2 border-2 rounded-xl px-4 py-3 ${isEmpresa ? "border-municipal bg-municipal/5" : "border-gray-200"}`}>
                <RadioGroupItem value="empresa" id="checkin-solicitante-empresa" data-testid="checkin-solicitante-empresa" />
                <Label htmlFor="checkin-solicitante-empresa" className="font-normal cursor-pointer text-sm">Soy una empresa</Label>
              </div>
            </RadioGroup>
          </div>

          {isEmpresa ? (
            <div className="grid sm:grid-cols-2 gap-5 mb-5">
              <div>
                <Label className="mb-1.5 block">Razón social</Label>
                <Input value={form.razon_social} onChange={(e) => update({ razon_social: e.target.value })} placeholder="Nombre de la empresa" data-testid="checkin-razon-social-input" />
              </div>
              <div>
                <Label className="mb-1.5 block">CIF</Label>
                <Input value={form.cif} onChange={(e) => update({ cif: e.target.value.toUpperCase() })} placeholder="B12345678" data-testid="checkin-cif-input" />
                {form.cif && !validateCif(form.cif) && <p className="text-xs text-red-500 mt-1">CIF no válido</p>}
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5 mb-5">
              <div>
                <Label className="mb-1.5 block">Nombre y apellidos</Label>
                <Input value={form.nombre_apellidos} onChange={(e) => update({ nombre_apellidos: e.target.value })} data-testid="checkin-nombre-input" />
              </div>
              <div>
                <Label className="mb-1.5 block">DNI / NIE</Label>
                <Input value={form.dni_nie} onChange={(e) => update({ dni_nie: e.target.value.toUpperCase() })} placeholder="12345678A" data-testid="checkin-dni-input" />
                {form.dni_nie && !dniValid && <p className="text-xs text-red-500 mt-1">DNI/NIE no válido</p>}
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-[2fr_1fr_1fr_1fr] gap-5 mb-5">
            <div>
              <Label className="mb-1.5 block">Dirección</Label>
              <Input value={form.direccion} onChange={(e) => update({ direccion: e.target.value })} data-testid="checkin-direccion-input" />
            </div>
            <div><Label className="mb-1.5 block">Nº</Label><Input value={form.numero} onChange={(e) => update({ numero: e.target.value })} data-testid="checkin-numero-input" /></div>
            <div><Label className="mb-1.5 block">Piso</Label><Input value={form.piso} onChange={(e) => update({ piso: e.target.value })} data-testid="checkin-piso-input" /></div>
            <div><Label className="mb-1.5 block">C.P.</Label><Input value={form.codigopostal} onChange={(e) => update({ codigopostal: e.target.value })} data-testid="checkin-cp-input" /></div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mb-5">
            <div>
              <Label className="mb-1.5 block">Correo electrónico (opcional)</Label>
              <Input type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} placeholder="Déjalo en blanco si no tienes" data-testid="checkin-email-input" />
            </div>
          </div>

          <div className="mb-5">
            <Label className="mb-1.5 block">Detalles adicionales (opcional)</Label>
            <Textarea value={form.detalles} onChange={(e) => update({ detalles: e.target.value })} data-testid="checkin-detalles-textarea" />
          </div>

          <div className="border-t border-gray-100 pt-5 mb-5">
            <Label className="text-sm font-semibold text-municipal mb-3 block">¿Qué residuos vas a traer?</Label>
            {catalogo.length > 0 && (
              <ResiduosSelector ref={residuosSelectorRef} catalogo={catalogo} value={residuos} onChange={setResiduos} onPendingChange={setHasPendingResiduo} />
            )}
          </div>

          {!isEmpresa && (
            <div className="flex items-start gap-3 mb-3">
              <Checkbox id="apuntado" checked={form.apuntado} onCheckedChange={(v) => update({ apuntado: v })} data-testid="checkin-apuntado-checkbox" />
              <Label htmlFor="apuntado" className="font-normal text-sm text-gray-600 leading-relaxed cursor-pointer">
                Quiero apuntarme a la bonificación de la tasa de residuos y soy el sujeto pasivo del domicilio indicado.
              </Label>
            </div>
          )}
          <div className="flex items-start gap-3 mb-8">
            <Checkbox id="lopd" checked={form.acepta_clausula} onCheckedChange={(v) => update({ acepta_clausula: v })} data-testid="checkin-lopd-checkbox" />
            <Label htmlFor="lopd" className="font-normal text-sm text-gray-600 leading-relaxed cursor-pointer">
              He leído y acepto la política de privacidad. El responsable del tratamiento es el
              Ayuntamiento de San Fernando de Henares.
            </Label>
          </div>

          {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

          <Button type="submit" disabled={!canSubmit || submitting} className="w-full bg-municipal hover:bg-municipal-dark text-white" data-testid="checkin-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Registrar visita
          </Button>
        </form>
      </section>
      <QrScannerModal open={showScanner} onClose={() => setShowScanner(false)} onResult={handleQrScanned} />
      <GenerarQrModal open={showGenerarQr} onClose={() => setShowGenerarQr(false)} />
      <Footer />
    </div>
  );
}
